/* eslint-disable max-lines-per-function */
import React, { useEffect } from "react";
import { Form, Formik, useFormikContext } from "formik";
import * as yup from "yup";
import { FormControlLabel, Grid, Radio, RadioGroup, Tooltip } from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import { useQuery } from "@apollo/client";
import { InputWithErrorLabel as Input } from "components/haven-design/form/inputs";
import {
  getMessageGroups,
  getPropertyUseCases_getActiveSubaccounts as SubaccountType,
  getPropertyUseCases_property_propertyUseCases as gqlPropertyUseCaseType,
  getPropertyUseCases_getUseCases as gqlUseCaseType,
} from "types/gql-types";
import { Checkbox } from "components/haven-design/form";
import { MaterialFormikSelectAutocomplete } from "components/haven-design/form/select";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import { GET_MESSAGE_GROUPS } from "./queries";

export type UseCaseType = gqlUseCaseType;
export type PropertyUseCaseType = gqlPropertyUseCaseType;

const validationSchema = yup.object({
  messageId: yup.string().required(),
  smsPrefix: yup.string().nullable(true),
  applicationId: yup.string().nullable(true),
  active: yup.boolean().required(),
});

type FormPropsType = {
  id: string,
  initialValues: PropertyUseCaseType,
  useCases: UseCaseType[],
  subaccounts: SubaccountType[],
  onSubmit: (input: PropertyUseCaseType) => void,
}

const MSG_GROUP = "group";
const MSG_NUMBER = "number";

const UseCaseFormMessagingSettings: React.FC = () => {
  const { values, setValues } = useFormikContext<PropertyUseCaseType>();
  const { applicationId, messageId } = values;
  const [messageIdType, setMessageIdType] = React.useState<string>(messageId.startsWith("+1") ? MSG_NUMBER : MSG_GROUP);
  const { error, loading, data, refetch } = useQuery<getMessageGroups>(GET_MESSAGE_GROUPS, {
    variables: { id: values.applicationId },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    refetch({ variables: { id: values.applicationId } });
  }, [applicationId]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ServerError desc={error.message} />;
  if (!data) return <ServerError desc="Missing Data" />;

  return (
    <>
      <Grid container item xs={12}>
        <RadioGroup
          row
          value={messageIdType}
          onChange={(_, value) => {
            setValues({ ...values, messageId: "" });
            setMessageIdType(value);
          }}>
          <FormControlLabel label="Message Group" value={MSG_GROUP} control={<Radio />} />
          <FormControlLabel label="Phone Number" value={MSG_NUMBER} control={<Radio />} />
        </RadioGroup>
      </Grid>
      {messageIdType === MSG_GROUP &&
      <Grid container item xs={12}>
        <MaterialFormikSelectAutocomplete
          required
          name="messageId"
          placeholder="Message Group"
          label="Message Group"
          options={data?.getMessageGroups.map(({ name: label, id: value }) => ({ label, value }))}
          fullWidth
        />
      </Grid>}
      {messageIdType === MSG_NUMBER &&
      <Grid container item xs={12}>
        <MaterialFormikSelectAutocomplete
          required
          name="messageId"
          placeholder="Phone Number"
          label="Phone Number"
          options={data?.getSmsPhoneNumbers.map(({ name: label, id: value }) => ({ label, value }))}
          fullWidth
        />
      </Grid>}
    </>
  );
};

const UseCaseForm: React.FC<FormPropsType> =
({ id, onSubmit, initialValues, useCases, subaccounts }) => <Formik
  initialValues={initialValues as unknown as PropertyUseCaseType}
  validationSchema={validationSchema}
  onSubmit={(input:PropertyUseCaseType) => onSubmit(input)}
>
  {({ handleSubmit }) => (
    <Form onSubmit={handleSubmit} id={id}>
      <Grid container spacing={2}>
        <Grid container item xs={12}>
          <MaterialFormikSelectAutocomplete
            name="useCaseId"
            placeholder="Use Case *"
            label="Use Case *"
            options={useCases.map(({ friendlyName: label, id: value }) => ({ label, value }))}
            fullWidth
          />
        </Grid>
        <Grid container item xs={12}>
          <MaterialFormikSelectAutocomplete
            name="applicationId"
            placeholder="Subaccount"
            label="Subaccount"
            options={subaccounts.map(({ name: label, id: value }) => ({ label, value }))}
            fullWidth
          />
        </Grid>
        <UseCaseFormMessagingSettings />
        <Grid container item xs={12}>
          <Input margin="none" label="SMS Prefix Override" name="smsPrefix" />
        </Grid>
        <Grid container item xs={4} md={4}>
          <Checkbox name="active" label={<>Active <Tooltip title="If unchecked the use case will not be active.">
            <InfoIcon fontSize="small" color="primary"/>
          </Tooltip></>} />

        </Grid>
      </Grid>
    </Form>
  )}</Formik>;

export default UseCaseForm;
